import { useMemo } from 'react'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { FilterIdentifier } from 'models/saved_view'
import { SelectFilter } from 'components/Filters/fields/SelectFilter'
import type { SelectFilterOption } from 'components/Filters/fields/SelectFilter'
import { DateRangeFilterWithPresets } from 'components/Filters/fields/DateRangeFilterWithPresets'
import { useGetProductsOptions } from '../../services/samplesApi'
import { useGetUserOptions } from '../../services/samplesApi'
import apiService from '../../services/api'
import { useGetFileExtensionOptions } from '../../services/documentsApi'
import { useBrandOptions } from '../../hooks/useBrandOptions'
import { featureFlagService } from '../../utils/featureFlagService'

export function DocumentsFilterset({
  filterIdentifier,
}: {
  filterIdentifier: FilterIdentifier
}) {
  const documentsFilterStore = filterStoreRepo.getStore(filterIdentifier)
  const api = apiService()
  const { enableBrandManagement } = featureFlagService()

  // Fetch tag options using apiService (same as ContactsFilterset)
  const { data: documentTagsOption } = api.useGetTagsOptions(true, 'documents')

  // Fetch file extension options from backend
  const { data: fileExtensionOptions } = useGetFileExtensionOptions()

  // Fetch product options
  const { data: rawProductOptions } = useGetProductsOptions(true)

  const productOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawProductOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawProductOptions])

  // Fetch brand options (only when brand management is enabled)
  const { brandOptions: rawBrandOptions } = useBrandOptions(
    enableBrandManagement
  )
  const brandOptions: SelectFilterOption[] = useMemo(
    () =>
      rawBrandOptions.map((b) => ({ label: b.label, value: String(b.value) })),
    [rawBrandOptions]
  )

  // Fetch user options
  const { data: rawUserOptions } = useGetUserOptions({
    onlyActive: true,
    withNoneOption: false,
  })

  const userOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawUserOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawUserOptions])

  return (
    <FilterModal identifier={filterIdentifier} store={documentsFilterStore}>
      <FilterGroup title="Document Filters" className="lg:grid-cols-2">
        <SelectFilter
          title="Tags"
          filterKey="tags"
          filterStore={documentsFilterStore}
          options={documentTagsOption as any}
        />
        <SelectFilter
          title="File Type"
          filterKey="file_extension"
          filterStore={documentsFilterStore}
          options={fileExtensionOptions || []}
        />
        <SelectFilter
          title="Product"
          filterKey="product"
          filterStore={documentsFilterStore}
          options={productOptions || []}
        />
        <SelectFilter
          title="Company"
          filterKey="contact_company"
          filterStore={documentsFilterStore}
          options={[]}
        />
        {enableBrandManagement && (
          <SelectFilter
            title="Brand"
            filterKey="brand"
            filterStore={documentsFilterStore}
            options={brandOptions}
          />
        )}
      </FilterGroup>

      <FilterGroup title="User & Date" className="lg:grid-cols-2">
        <SelectFilter
          title="Uploaded By"
          filterKey="created_by"
          filterStore={documentsFilterStore}
          options={userOptions || []}
        />
        <DateRangeFilterWithPresets
          title="Upload Date"
          filterKey="created"
          filterStore={documentsFilterStore}
        />
        <DateRangeFilterWithPresets
          title="Last Updated"
          filterKey="modified"
          filterStore={documentsFilterStore}
        />
      </FilterGroup>
    </FilterModal>
  )
}
